import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Row, Col, Button, Card, Typography, Space, Rate, Tag, Avatar, Divider, Spin, Carousel, Skeleton } from 'antd';
import {
  ThunderboltOutlined, SafetyCertificateOutlined, ClockCircleOutlined,
  HeartOutlined, CheckCircleFilled, WhatsAppOutlined, EnvironmentOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import VegetableCard from '../components/VegetableCard';
import BannerSlider from '../components/BannerSlider';
import PromoStrip from '../components/PromoStrip';
import { fetchVegetables, fetchComboPacks, fetchTestimonials, fetchFarmers, fetchDeliverySlots, fetchHomeStats, Testimonial, HomepageFarmer, DeliverySlot, HomeStats } from '../services/productService';
import { Vegetable, ComboPack } from '../types/product';
import { useCart } from '../context/CartContext';
import { WHATSAPP_URL, API_URL } from '../config/app.config';
import '../styles/HomePage.css';

const { Title, Text, Paragraph } = Typography;

const STATIC_MARQUEE = [
  '🧑‍🌾 Partner Farmers', '🚚 Weekend Delivery',
  '💚 Zero Middlemen', '🌾 Harvested Daily', '🛒 Easy Online Order',
];

function fmtStat(n: number | null, suffix = '+'): string {
  if (n == null || n === 0) return '—';
  return `${n}${suffix}`;
}

function fmtTime(t: string): string {
  const h = parseInt(t.split(':')[0], 10);
  return h === 0 ? '12AM' : h < 12 ? `${h}AM` : h === 12 ? '12PM' : `${h - 12}PM`;
}

function slotTiming(slot: DeliverySlot): string {
  const dayShort = slot.day.slice(0, 3);
  const label = slot.label ? slot.label.replace(/^(Saturday|Sunday)\s*/i, '') : '';
  return `${dayShort}${label ? ' ' + label : ''}: ${fmtTime(slot.time_from)}–${fmtTime(slot.time_to)}`;
}

// Shown only if the slots call fails. Deliberately vague: inventing specific
// weekend times would be wrong for daily-delivery pincodes, and a wrong time is
// worse than no time.
const FALLBACK_TIMINGS = ['Daily & weekend slots', 'Exact times shown at checkout'];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const [vegetables, setVegetables] = useState<Vegetable[]>([]);
  const [comboPacks, setComboPacks] = useState<ComboPack[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[] | null>(null);
  const [homeFarmers, setHomeFarmers] = useState<HomepageFarmer[] | null>(null);
  const [deliverySlots, setDeliverySlots] = useState<DeliverySlot[]>([]);
  const [homeStats, setHomeStats] = useState<HomeStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchVegetables().catch(() => [] as Vegetable[]),
      fetchComboPacks().catch(() => [] as ComboPack[]),
      fetchTestimonials().catch(() => [] as Testimonial[]),
      fetchFarmers().catch(() => [] as HomepageFarmer[]),
      fetchDeliverySlots().catch(() => [] as DeliverySlot[]),
      fetchHomeStats().catch(() => null),
    ]).then(([vegs, packs, reviews, farmerList, slots, stats]) => {
      setVegetables(vegs);
      setComboPacks(packs);
      setTestimonials(reviews);
      setHomeFarmers(farmerList);
      setDeliverySlots(slots);
      setHomeStats(stats);
    }).finally(() => setLoading(false));
  }, []);

  // Scroll to a hash target (e.g. /#how-it-works) — re-runs after content loads so
  // the position is correct once the async sections above have rendered.
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [location.hash, loading]);

  const heroVeggies = vegetables.slice(0, 6).map(v => ({
    e: v.emoji,
    n: v.name,
    p: `₹${v.price}/${v.unit}`,
  }));

  const marqueeItems: { label: string; id?: number }[] = [
    ...vegetables.slice(0, 8).map(v => ({ label: `${v.emoji} ${v.name}`, id: v.id })),
    ...STATIC_MARQUEE.map(label => ({ label })),
  ];

  const statFamilies  = fmtStat(homeStats?.families ?? null);
  const statFarmers   = fmtStat(homeStats?.farmers  ?? null);
  const statProducts  = fmtStat(homeStats?.products  ?? null);
  const statRating    = homeStats?.avg_rating ? `${homeStats.avg_rating}⭐` : '4.9⭐';

  const statsBar = [
    { num: statFamilies, label: 'Happy Families',   icon: '👨‍👩‍👧' },
    { num: statFarmers,  label: 'Partner Farmers',  icon: '🧑‍🌾' },
    { num: statRating,   label: 'Average Rating',   icon: '⭐' },
    { num: statProducts, label: 'Fresh Vegetables', icon: '🥬' },
  ];

  const trustCards = [
    { icon: <ThunderboltOutlined className="icon-green icon-xl" />, iconCls: 'trust-icon-green', title: 'Farm Fresh', desc: 'Harvested within 24 hours of delivery' },
    { icon: <SafetyCertificateOutlined className="icon-orange icon-xl" />, iconCls: 'trust-icon-orange', title: 'Direct from Farmers', desc: 'No middlemen, fair prices for farmers' },
    { icon: <ClockCircleOutlined className="icon-green icon-xl" />, iconCls: 'trust-icon-green', title: 'Weekend Delivery', desc: 'Saturday & Sunday doorstep delivery' },
    { icon: <HeartOutlined className="icon-orange icon-xl" />, iconCls: 'trust-icon-orange', title: 'No Long Storage', desc: 'Fresh to your kitchen in hours' },
  ];

  const steps = [
    { step: 1, emoji: '🛒', title: 'Browse & Add', desc: 'Choose from 30+ fresh vegetables and combo packs' },
    { step: 2, emoji: '📅', title: 'Order Mon–Fri', desc: 'Place your order anytime Monday to Friday' },
    { step: 3, emoji: '🌾', title: 'Farm Harvest', desc: 'Farmers harvest fresh on Friday/Saturday morning' },
    { step: 4, emoji: '🚚', title: 'Weekend Delivery', desc: 'Fresh vegetables delivered to your door Saturday or Sunday' },
  ];

  const deliveryAreas = [
    'Delhi (All Areas)', 'Noida & Greater Noida', 'Gurgaon / Gurugram',
    'Faridabad', 'Ghaziabad', 'Indirapuram', 'Vasundhara', 'Dwarka',
  ];

  // Split fruits out of the vegetable rails so they don't show in both sections.
  const veggies = vegetables.filter(v => v.category !== 'fruit');
  const fruits = vegetables.filter(v => v.category === 'fruit');
  const featuredVeggies = veggies.filter(v => v.featured).length > 0
    ? veggies.filter(v => v.featured) : veggies.slice(0, 6);
  // Featured fruits, falling back to all fruits when none are flagged.
  const featuredFruits = fruits.filter(v => v.featured).length > 0
    ? fruits.filter(v => v.featured) : fruits;

  return (
    <div className="page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="hero-gradient home-hero">
        <div className="section-inner">
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} md={12}>
              <Tag color="success" className="home-hero-tag">
                🌱 Now delivering in Delhi/NCR
              </Tag>
              <Title className="hero-headline home-hero-title">
                Fresh Vegetables<br />
                <span className="home-hero-accent">Farm to Kitchen</span> 🥦
              </Title>
              <p className="home-hero-tagline">Real farms. Real fresh. Really you.</p>
              <Paragraph className="home-hero-para">
                Order during the week and get freshly harvested vegetables delivered on weekends. Supporting local farmers, feeding happy families.
              </Paragraph>
              <Space size={12} wrap>
                <Button size="large" className="green-btn home-hero-order-btn" icon={<ShoppingCartOutlined />}
                  onClick={() => navigate('/shop')}>
                  Order Now
                </Button>
                {comboPacks.length > 0 && (
                  <Button size="large" onClick={() => navigate('/shop?tab=packs')} className="home-hero-packs-btn">
                    View Weekly Packs →
                  </Button>
                )}
              </Space>
              <div className="home-hero-stats">
                <div>
                  <Text strong className="text-green home-hero-stat-num">{statFamilies}</Text>
                  <Text className="home-hero-stat-label">Happy Families</Text>
                </div>
                <div>
                  <Text strong className="text-green home-hero-stat-num">{statFarmers}</Text>
                  <Text className="home-hero-stat-label">Partner Farmers</Text>
                </div>
                <div>
                  <Text strong className="text-orange home-hero-stat-num">{statRating}</Text>
                  <Text className="home-hero-stat-label">Avg Rating</Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <BannerSlider
                pageKey="home_hero"
                fallback={
                  <div className="home-hero-visual">
                    <div className="home-hero-fresh-header">
                      <span className="home-hero-fresh-tag">🌿 Fresh This Week</span>
                      <span className="home-orders-badge">✅ Open</span>
                    </div>
                    <div className="home-hero-veg-showcase">
                      {loading
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="home-hero-veg-item">
                              <Skeleton.Avatar active size={32} shape="circle" />
                              <Skeleton.Input active size="small" style={{ width: 50, marginTop: 4 }} />
                            </div>
                          ))
                        : heroVeggies.map((v, i) => (
                            <div key={i} className="home-hero-veg-item">
                              <span className="home-hero-veg-emoji">{v.e}</span>
                              <span className="home-hero-veg-name">{v.n}</span>
                              <span className="home-hero-veg-price">{v.p}</span>
                            </div>
                          ))
                      }
                    </div>
                    <div className="home-hero-visual-footer">
                      <div className="home-hero-visual-stat">
                        <span className="home-hero-visual-stat-num">{statFamilies}</span>
                        <span className="home-hero-visual-stat-label">Families</span>
                      </div>
                      <div className="home-hero-visual-divider" />
                      <div className="home-hero-visual-stat">
                        <span className="home-hero-visual-stat-num">{statFarmers}</span>
                        <span className="home-hero-visual-stat-label">Farmers</span>
                      </div>
                      <div className="home-hero-visual-divider" />
                      <div className="home-hero-visual-stat">
                        <span className="home-hero-visual-stat-num">{statRating}</span>
                        <span className="home-hero-visual-stat-label">Rating</span>
                      </div>
                    </div>
                  </div>
                }
              />
            </Col>
          </Row>
        </div>
      </section>

      {/* MARQUEE TICKER */}
      <div className="home-marquee-strip">
        <div className="home-marquee-inner">
          {[...marqueeItems, ...marqueeItems].map((item, i) =>
            item.id ? (
              <Link key={i} to={`/vegetable/${item.id}`} className="home-marquee-item home-marquee-item--veg">
                {item.label}
              </Link>
            ) : (
              <span key={i} className="home-marquee-item">{item.label}</span>
            )
          )}
        </div>
      </div>

      {/* TRUST */}
      <section className="home-trust-section">
        <div className="section-inner">
          <Row gutter={[20, 20]}>
            {trustCards.map((card, i) => (
              <Col xs={12} md={6} key={i}>
                <Card className="trust-card home-trust-card" hoverable>
                  <div className={`home-trust-icon ${card.iconCls}`}>{card.icon}</div>
                  <Title level={5} className="font-poppins home-trust-title">{card.title}</Title>
                  <Text className="text-muted text-sm">{card.desc}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* STATS BAR */}
      <div className="home-stats-bar">
        <div className="section-inner">
          <Row justify="center" gutter={[48, 20]}>
            {statsBar.map((stat, i) => (
              <Col key={i} xs={12} sm={6}>
                <div className="home-stats-bar-item">
                  <span className="home-stats-bar-icon">{stat.icon}</span>
                  <span className="home-stats-bar-num">{stat.num}</span>
                  <span className="home-stats-bar-label">{stat.label}</span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="home-how-section">
        <div className="section-inner section-center">
          <Tag color="green" className="home-tag-pill">Simple Process</Tag>
          <Title className="section-title home-section-title-mb">How It Works</Title>
          <Text className="section-subtitle">From your screen to your kitchen in just 4 steps</Text>
          <Row gutter={[24, 24]} className="home-how-steps">
            {steps.map((s, i) => (
              <Col xs={24} sm={12} md={6} key={i}>
                <Card className="home-step-card">
                  <div className="home-step-emoji">{s.emoji}</div>
                  <div className="step-number">{s.step}</div>
                  <Title level={5} className="home-step-title">{s.title}</Title>
                  <Text className="home-step-desc">{s.desc}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* WEEKLY PACKS — hidden until at least one pack exists */}
      {comboPacks.length > 0 && (
      <section className="home-packs-section">
        <div className="section-inner">
          <div className="home-packs-header">
            <Tag color="orange" className="home-tag-pill">Best Value</Tag>
            <Title className="section-title">Weekly Family Packs</Title>
            <Text className="section-subtitle">Pre-curated vegetable combos for every family size</Text>
          </div>
          {loading ? (
            <div className="home-loading"><Spin size="large" /></div>
          ) : comboPacks.length === 0 ? (
            <div className="home-packs-coming-soon">
              <div className="home-packs-cs-emoji">🧺</div>
              <Title level={4} className="home-packs-cs-title">Coming Soon!</Title>
              <Text className="home-packs-cs-text">
                We're curating the perfect weekly veggie packs for your family.<br />
                Check back soon or order individual vegetables from our shop.
              </Text>
              <Button size="large" className="green-btn home-packs-cs-btn" onClick={() => navigate('/shop')}>
                Shop Individual Vegetables →
              </Button>
            </div>
          ) : (
            <Row gutter={[24, 24]} justify="center">
              {comboPacks.map(pack => (
                <Col xs={24} sm={8} key={pack.id}>
                  <div className={`home-pack-card${pack.featured ? ' featured' : ''}`}>
                    {pack.featured && <div className="home-pack-popular-badge">⭐ MOST POPULAR</div>}
                    <div className="home-pack-emoji">{pack.emoji}</div>
                    <Title level={4} className="font-poppins home-pack-name">{pack.name}</Title>
                    <Text className="home-pack-members">👨‍👩‍👧 {pack.members}</Text>
                    <div className="home-pack-items">
                      {pack.items.map((item, i) => (
                        <div key={i} className="home-pack-item-row">
                          <CheckCircleFilled className="icon-light-green icon-sm" />
                          <Text className="text-sm">{item}</Text>
                        </div>
                      ))}
                    </div>
                    <Divider className="divider-sm" />
                    <div className="home-pack-price-row">
                      <Text delete className="home-pack-original">₹{pack.originalPrice}</Text>
                      <Text strong className="home-pack-price">₹{pack.price}</Text>
                      <Text className="home-pack-save">Save ₹{pack.originalPrice - pack.price}</Text>
                    </div>
                    <Button block className="green-btn" size="large"
                      onClick={() => addToCart({ id: pack.id, name: pack.name, price: pack.price, unit: 'pack', emoji: pack.emoji, farm: 'Multiple Farms', stock: 999 })}>
                      Add Pack to Cart
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </section>
      )}

      {/* FEATURED VEGETABLES */}
      <section className="home-featured-section">
        <div className="section-inner">
          {/* Running promos sit directly above the products they point at. */}
          <PromoStrip />
          <div className="home-featured-header">
            <div>
              <Tag color="green" className="home-tag-pill">Fresh Today</Tag>
              <Title className="section-title home-featured-title">Featured Vegetables</Title>
            </div>
            <Button onClick={() => navigate('/shop')} className="home-featured-view-btn">
              View All Vegetables →
            </Button>
          </div>
          {loading ? (
            <div className="home-loading"><Spin size="large" /></div>
          ) : (
            <Row gutter={[20, 20]}>
              {featuredVeggies.map(veg => (
                <Col xs={12} sm={8} md={4} key={veg.id}>
                  <VegetableCard {...veg} />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </section>

      {/* FEATURED FRUITS */}
      {!loading && featuredFruits.length > 0 && (
        <section className="home-featured-section">
          <div className="section-inner">
            <div className="home-featured-header">
              <div>
                <Tag color="volcano" className="home-tag-pill">Fresh Fruits</Tag>
                <Title className="section-title home-featured-title">Featured Fruits</Title>
              </div>
              <Button onClick={() => navigate('/shop?group=fruit')} className="home-featured-view-btn">
                View All Fruits →
              </Button>
            </div>
            <Row gutter={[20, 20]}>
              {featuredFruits.map(fruit => (
                <Col xs={12} sm={8} md={4} key={fruit.id}>
                  <VegetableCard {...fruit} />
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {/* FARMER STORIES */}
      {homeFarmers && homeFarmers.length > 0 && (
        <section className="home-farmers-section">
          <div className="section-inner">
            <div className="home-farmer-header">
              <Tag color="green" className="home-tag-pill">Our Partners</Tag>
              <Title className="section-title">Meet Our Farmers</Title>
              <Text className="section-subtitle">The people who grow your food with love and care</Text>
            </div>
            <Carousel
              autoplay
              autoplaySpeed={4000}
              dots
              slidesToShow={Math.min(homeFarmers.length, 3)}
              slidesToScroll={1}
              responsive={[
                { breakpoint: 768, settings: { slidesToShow: 1 } },
                { breakpoint: 1024, settings: { slidesToShow: 2 } },
              ]}
              style={{ paddingBottom: 32 }}
            >
              {homeFarmers.map(farmer => (
                <div key={farmer.id} style={{ padding: '0 12px' }}>
                  <Card className="farmer-card home-farmer-card" style={{ margin: '0 8px' }}>
                    <div className="home-farmer-center">
                      {farmer.photo_url
                        ? <img
                            src={`${API_URL}${farmer.photo_url}`}
                            alt={farmer.name}
                            className="home-farmer-photo"
                            loading="lazy"
                            decoding="async"
                          />
                        : <div className="home-farmer-emoji">{farmer.emoji || '👨‍🌾'}</div>
                      }
                      <Title level={5} className="font-poppins home-farmer-name">{farmer.name}</Title>
                      <Text className="home-farmer-village">
                        <EnvironmentOutlined /> {farmer.village}{farmer.state ? `, ${farmer.state}` : ''}
                      </Text>
                    </div>
                    {farmer.story && (
                      <div
                        className="home-farmer-para rich-text-display"
                        dangerouslySetInnerHTML={{ __html: farmer.story }}
                      />
                    )}
                    {farmer.available_produce && (
                      <div className="home-farmer-tag-row">
                        <Tag color="green" className="home-farmer-produce-tag">🥬 {farmer.available_produce}</Tag>
                      </div>
                    )}
                  </Card>
                </div>
              ))}
            </Carousel>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Link to="/our-farmers" className="home-view-all-link">
                Meet All Our Farmers →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* DELIVERY AREAS */}
      <section className="home-delivery-section">
        <div className="section-inner">
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} md={12}>
              <Tag color="green" className="home-tag-pill">Coverage</Tag>
              <Title className="section-title home-delivery-title">Delivering Across Delhi/NCR 📍</Title>
              <Text className="section-subtitle home-delivery-subtitle">
                Currently serving all of Delhi/NCR. Expanding every week!
              </Text>
              <Row gutter={[12, 12]}>
                {deliveryAreas.map(area => (
                  <Col key={area} xs={12}>
                    <div className="home-delivery-area-item">
                      <CheckCircleFilled className="home-delivery-area-check" />
                      <Text className="home-delivery-area-text">{area}</Text>
                    </div>
                  </Col>
                ))}
              </Row>
              <div className="home-delivery-timing-box">
                <Text className="home-delivery-timing-label">⏰ Delivery Timings:</Text>
                <div className="home-delivery-timing-grid">
                  {(deliverySlots.length > 0 ? deliverySlots.map(slotTiming) : FALLBACK_TIMINGS).map(t => (
                    <Text key={t} className="home-delivery-timing-text">🕐 {t}</Text>
                  ))}
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="home-delivery-notify-box">
                <div className="home-delivery-notify-emoji">🗺️</div>
                <Title level={4} className="home-delivery-notify-title">
                  Not in your area yet?
                </Title>
                <Text className="home-delivery-notify-text">
                  Drop us a WhatsApp and we'll notify you when we expand!
                </Text>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
                  <Button size="large" icon={<WhatsAppOutlined />} className="home-delivery-notify-btn">
                    Notify Me on WhatsApp
                  </Button>
                </a>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials && testimonials.length > 0 && (
        <section className="home-testimonial-section">
          <div className="section-inner">
            <div className="home-testimonial-header">
              <Tag color="gold" className="home-tag-pill">Happy Customers</Tag>
              <Title className="section-title">What Families Say</Title>
            </div>
            <Row gutter={[24, 24]}>
              {testimonials.map(t => (
                <Col xs={24} md={8} key={t.id}>
                  <Card className="home-testimonial-card">
                    <Rate disabled defaultValue={t.rating} className="home-testimonial-rating" />
                    <Paragraph className="home-testimonial-review">"{t.review}"</Paragraph>
                    <div className="home-testimonial-author">
                      <Avatar className="home-testimonial-avatar">{t.name[0]}</Avatar>
                      <div>
                        <Text strong className="text-block">{t.name}</Text>
                        {t.location && <Text className="text-muted text-xs text-block">{t.location}</Text>}
                        {t.society && <Text className="home-testimonial-society">🏢 {t.society}</Text>}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="home-cta-section">
        <div className="home-cta-inner">
          <Title className="home-cta-title">Ready for Farm-Fresh Vegetables? 🥬</Title>
          <Text className="home-cta-sub">Join {statFamilies} families getting fresh vegetables every weekend.</Text>
          <Space size={16} wrap className="home-cta-space">
            <Button size="large" className="home-cta-order-btn" onClick={() => navigate('/shop')}>
              🛒 Start Ordering
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <Button size="large" className="home-cta-wa-btn" icon={<WhatsAppOutlined />}>
                WhatsApp Us
              </Button>
            </a>
          </Space>
        </div>
      </section>

      <Footer />
      <a href={WHATSAPP_URL} className="whatsapp-float" target="_blank" rel="noreferrer">
        <WhatsAppOutlined />
      </a>
    </div>
  );
};

export default HomePage;
